import React, { useState, useContext } from 'react';
import { Box, Typography, Paper, IconButton, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip, Chip } from '@mui/material';
import { Done, DoneAll, Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon, Check as CheckIcon, AddReaction as AddReactionIcon, InsertDriveFile as FileIcon, Download as DownloadIcon } from '@mui/icons-material';
import { ChatContext } from '../../contexts/ChatContext';
import { AuthContext } from '../../contexts/AuthContext';
import EmojiPickerPopover from './EmojiPickerPopover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

const MessageBubble = ({ message, isOwnMessage }) => {
  const { editMessage, deleteMessage, addReaction } = useContext(ChatContext);
  const { user, token } = useContext(AuthContext);
  const [isHovering, setIsHovering] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(message.content || '');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reactionAnchorEl, setReactionAnchorEl] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Check if attachment is an image
  const isImageAttachment = (attachment) => {
    if (!attachment) return false;
    const imageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    return imageTypes.includes(attachment.mime_type?.toLowerCase());
  };

  // Get file URL
  const getFileUrl = (attachment) => {
    if (!attachment) return null;
    return `${BACKEND_URL}/api/chat/files/${attachment.filename}`;
  };

  // Handle download
  const handleDownload = async (attachment) => {
    if (!attachment || !token) return;

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/chat/attachments/${attachment.id}/download`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.original_filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleEditClick = () => {
    setEditedContent(message.content || '');
    setIsEditing(true);
  };

  const handleEditSave = () => {
    if (editedContent.trim() && editedContent !== message.content) {
      editMessage(message.id, editedContent.trim());
    }
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setEditedContent(message.content || '');
    setIsEditing(false);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteMessage(message.id);
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEditSave();
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  const handleReactionClick = (event) => {
    setReactionAnchorEl(event.currentTarget);
  };

  const handleReactionClose = () => {
    setReactionAnchorEl(null);
  };

  const handleReactionSelect = (emojiObject) => {
    addReaction(message.id, emojiObject.emoji);
    handleReactionClose();
  };

  const handleReactionToggle = (emoji) => {
    // Toggle reaction: add if not present, remove if present
    addReaction(message.id, emoji);
  };

  // Deleted message display
  if (message.is_deleted) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
          mb: 1
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: '70%',
            px: 2,
            py: 1,
            backgroundColor: '#f5f5f5',
            borderRadius: 2,
            borderTopRightRadius: isOwnMessage ? 0 : 2,
            borderTopLeftRadius: isOwnMessage ? 2 : 0,
            border: '1px dashed #ccc'
          }}
        >
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            [Nachricht gelöscht]
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {formatTime(message.created_at)}
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
          mb: 1,
          position: 'relative'
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {/* Action buttons - show on hover and not in edit mode */}
        {isHovering && !isEditing && (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              mr: isOwnMessage ? 1 : 0,
              ml: isOwnMessage ? 0 : 1,
              order: isOwnMessage ? 0 : 1,
              alignItems: 'flex-start',
              pt: 1
            }}
          >
            <Tooltip title="Reagieren">
              <IconButton size="small" onClick={handleReactionClick} sx={{ p: 0.5 }}>
                <AddReactionIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {isOwnMessage && (
              <>
                <Tooltip title="Bearbeiten">
                  <IconButton size="small" onClick={handleEditClick} sx={{ p: 0.5 }}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Löschen">
                  <IconButton size="small" onClick={handleDeleteClick} sx={{ p: 0.5 }}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        )}

        <Paper
          elevation={1}
          sx={{
            maxWidth: '70%',
            px: 2,
            py: 1,
            backgroundColor: isOwnMessage ? '#e3f2fd' : '#f5f5f5',
            borderRadius: 2,
            borderTopRightRadius: isOwnMessage ? 0 : 2,
            borderTopLeftRadius: isOwnMessage ? 2 : 0
          }}
        >
          {isEditing ? (
            // Edit mode
            <Box>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                onKeyDown={handleKeyPress}
                autoFocus
                size="small"
                variant="standard"
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <IconButton size="small" onClick={handleEditCancel} color="default">
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleEditSave}
                  color="primary"
                  disabled={!editedContent.trim() || editedContent === message.content}
                >
                  <CheckIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          ) : (
            // Normal message display
            <>
              {/* Attachment Display */}
              {message.attachment && (
                <Box sx={{ mb: message.content ? 1 : 0 }}>
                  {isImageAttachment(message.attachment) ? (
                    // Image attachment - show inline
                    <Box
                      onClick={() => setImageDialogOpen(true)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.9 }
                      }}
                    >
                      <Box
                        component="img"
                        src={getFileUrl(message.attachment)}
                        alt={message.attachment.original_filename}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 300,
                          borderRadius: 1,
                          display: 'block'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </Box>
                  ) : (
                    // Other file types - show as download card
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1.5,
                        backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.05)',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.08)'
                        }
                      }}
                      onClick={() => handleDownload(message.attachment)}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: 'primary.main',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FileIcon sx={{ color: 'white' }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {message.attachment.original_filename}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(message.attachment.file_size)}
                        </Typography>
                      </Box>
                      <DownloadIcon sx={{ color: 'text.secondary' }} />
                    </Box>
                  )}
                </Box>
              )}

              {/* Text Content */}
              {message.content && (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {message.content}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
                {message.edited_at && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mr: 0.5 }}>
                    (bearbeitet)
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {formatTime(message.created_at)}
                </Typography>

                {isOwnMessage && (
                  message.is_read ? (
                    <DoneAll sx={{ fontSize: 16, color: '#1976d2' }} />
                  ) : (
                    <Done sx={{ fontSize: 16, color: 'text.secondary' }} />
                  )
                )}
              </Box>
            </>
          )}
        </Paper>

        {/* Reactions display */}
        {message.reactions && message.reactions.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              mt: 0.5,
              maxWidth: '70%'
            }}
          >
            {message.reactions.map((reaction) => {
              const hasUserReacted = reaction.reaction_ids && user && reaction.reaction_ids[user.id];
              return (
                <Chip
                  key={reaction.emoji}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{reaction.emoji}</span>
                      <Typography variant="caption">{reaction.count}</Typography>
                    </Box>
                  }
                  size="small"
                  onClick={() => handleReactionToggle(reaction.emoji)}
                  sx={{
                    height: 24,
                    backgroundColor: hasUserReacted ? '#e3f2fd' : 'background.paper',
                    border: hasUserReacted ? '1px solid #1976d2' : '1px solid #ddd',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: hasUserReacted ? '#bbdefb' : '#f5f5f5'
                    }
                  }}
                />
              );
            })}
          </Box>
        )}
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Nachricht löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchtest du diese Nachricht wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Abbrechen</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Emoji picker for reactions */}
      <EmojiPickerPopover
        anchorEl={reactionAnchorEl}
        open={Boolean(reactionAnchorEl)}
        onClose={handleReactionClose}
        onEmojiClick={handleReactionSelect}
      />

      {/* Image fullscreen dialog */}
      {message.attachment && isImageAttachment(message.attachment) && (
        <Dialog
          open={imageDialogOpen}
          onClose={() => setImageDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography noWrap sx={{ flex: 1 }}>
              {message.attachment.original_filename}
            </Typography>
            <Box>
              <IconButton onClick={() => handleDownload(message.attachment)} size="small">
                <DownloadIcon />
              </IconButton>
              <IconButton onClick={() => setImageDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Box
              component="img"
              src={getFileUrl(message.attachment)}
              alt={message.attachment.original_filename}
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default MessageBubble;
